import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, AlertCircle, ChevronRight, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsPage = () => {
  const [terms, setTerms] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/terms-and-conditions`);
      setTerms(response.data.terms_and_conditions);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error("Error fetching terms:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Parse terms into sections
  const parseTerms = (termsText) => {
    if (!termsText) return [];
    
    const sections = [];
    const lines = termsText.split('\n');
    let currentSection = null;
    
    lines.forEach(line => {
      line = line.trim();
      if (!line) return;
      
      // Check if it's a numbered section header
      const sectionMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (sectionMatch) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          number: sectionMatch[1],
          title: sectionMatch[2],
          content: []
        };
      } else if (currentSection) {
        currentSection.content.push(line);
      } else if (line.includes('IMPORTANT DISCLAIMER') || line.includes('Last Updated')) {
        sections.push({
          title: line,
          content: [],
          isHeader: true
        });
      } else {
        sections.push({
          title: null,
          content: [line]
        });
      }
    });
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    return sections;
  };

  const termsSections = parseTerms(terms);

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="terms-page">
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-8">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0F172A] font-medium">Terms & Conditions</span>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[#E15636] mb-4">
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-widest">Legal</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading tracking-tight text-[#0F172A] mb-4">
              Terms & <span className="text-[#E15636]">Conditions</span>
            </h1>
            <p className="text-[#64748B]">
              Last Updated: {lastUpdated || 'December 2024'}
            </p>
          </div>
          <div className="hidden md:flex gap-2 no-print">
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full"
              onClick={handlePrint}
              data-testid="print-terms-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </section>

      {/* Alert Banner */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mb-8">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading text-lg text-amber-800 mb-2">
                Important: Educational Purpose Only
              </h3>
              <p className="text-amber-700 leading-relaxed">
                The information provided on VisaFlow is for educational and informational purposes only. 
                Always verify visa requirements and application procedures with official embassy or consulate sources 
                before making any travel plans or submitting visa applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12" data-testid="terms-content">
          {loading ? (
            <div className="space-y-6">
              {[...Array(8)].map((_, i) => (
                <div key={i}>
                  <div className="skeleton h-6 w-48 mb-3"></div>
                  <div className="skeleton h-4 w-full mb-2"></div>
                  <div className="skeleton h-4 w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="terms-content space-y-8">
              {termsSections.map((section, index) => (
                <div key={index} className={section.isHeader ? 'pb-4 border-b border-slate-200' : ''}>
                  {section.isHeader ? (
                    <h2 className="text-2xl font-heading text-[#0F172A]">{section.title}</h2>
                  ) : section.number ? (
                    <>
                      <h2 className="text-xl font-heading text-[#0F172A] mb-3 flex items-start gap-3">
                        <span className="w-8 h-8 bg-[#E15636]/10 text-[#E15636] rounded-lg flex items-center justify-center text-sm font-medium flex-shrink-0">
                          {section.number}
                        </span>
                        {section.title}
                      </h2>
                      <div className="pl-11 space-y-2">
                        {section.content.map((line, lineIndex) => (
                          <p key={lineIndex} className="text-[#64748B] leading-relaxed">
                            {line.startsWith('-') || line.startsWith('•') ? (
                              <span className="flex items-start gap-2">
                                <span className="text-[#E15636] mt-1">•</span>
                                {line.replace(/^[-•]\s*/, '')}
                              </span>
                            ) : line}
                          </p>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      {section.content.map((line, lineIndex) => (
                        <p key={lineIndex} className="text-[#64748B] leading-relaxed">{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mt-12">
        <div className="bg-slate-50 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-heading text-[#0F172A] mb-3">Have Questions?</h3>
          <p className="text-[#64748B] mb-6">
            If you have any questions about these terms, please contact us at{' '}
            <a href="mailto:info@visaflows.ca" className="text-[#E15636] hover:underline">
              info@visaflows.ca
            </a>
          </p>
          <Link to="/visa-finder" data-testid="back-to-visa-finder">
            <Button className="rounded-full bg-[#0F172A] hover:bg-[#1E293B]">
              Start Your Visa Search
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
